import * as React from "react";

/** What {@link useCellDeps} returns: read `current` when a cell runs, not before. */
export type CellDeps<T> = { readonly current: T };

/**
 * Hands a component's latest state and callbacks to `DataTable` cell
 * renderers without rebuilding the column array.
 *
 * AG Grid treats a new column array as a structural change and resets the
 * user's column toggles, so a list builds its columns once (in a
 * `useState` initializer) and its cells read `deps.current` when they render
 * or when a click lands. `deps.current` is updated after every render.
 *
 * ```tsx
 * const deps = useCellDeps({ orgId, onDelete: setPendingDelete });
 * const [columns] = React.useState(() => buildColumns(deps));
 * ```
 *
 * A cell that displays a value from `deps` doesn't repaint when only that
 * value changes; put displayed values in the row data and keep `deps` for
 * callbacks and lookups.
 */
export function useCellDeps<T>(deps: T): CellDeps<T> {
  const ref = React.useRef(deps);
  React.useLayoutEffect(() => {
    ref.current = deps;
  });
  return ref;
}
